import type { NdtMethod } from "./ndt-procedure.js";

/**
 * Technical catalogs consumed by the END reports (Bloco 15): Normas de
 * Referência, Critérios de Aceitação and Acoplantes.
 *
 * These exist so the reports never hardcode technical content: every option
 * offered in a report comes from a record someone registered here. Company-
 * wide master data, like the procedures (no client scoping).
 */

export type NdtCatalogKey = "REFERENCE_STANDARD" | "ACCEPTANCE_CRITERIA" | "COUPLANT";

export const NDT_CATALOG_KEYS: readonly NdtCatalogKey[] = [
  "REFERENCE_STANDARD",
  "ACCEPTANCE_CRITERIA",
  "COUPLANT"
] as const;

export const NDT_CATALOG_LABELS: Record<NdtCatalogKey, string> = {
  REFERENCE_STANDARD: "Normas de Referência",
  ACCEPTANCE_CRITERIA: "Critérios de Aceitação",
  COUPLANT: "Acoplantes"
};

export const NDT_CATALOG_SINGULAR: Record<NdtCatalogKey, string> = {
  REFERENCE_STANDARD: "Norma de Referência",
  ACCEPTANCE_CRITERIA: "Critério de Aceitação",
  COUPLANT: "Acoplante"
};

/** Approved starting couplants — registered once as global catalog records, never hardcoded into the report. */
export const DEFAULT_COUPLANTS = ["GRAXA", "METIL", "ÁGUA"] as const;

/** Sentinel used by the report's couplant select to reveal a free-text field; never a catalog record. */
export const COUPLANT_OTHER = "OUTRO";

export type NdtCatalogItemStatus = "ACTIVE" | "INACTIVE";

export interface NdtCatalogItem {
  id: string;
  catalogKey: NdtCatalogKey;
  code: string;
  name: string;
  description: string;
  status: NdtCatalogItemStatus;
  /** Empty means the item applies to every END method. */
  applicableMethods: NdtMethod[];
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

/**
 * "Condição Superficial" of the ME report. This is the one enumeration the
 * specification approved as fixed — LIXADA and PINTADA, nothing else, and no
 * new option may be added automatically.
 */
export const ME_SURFACE_CONDITIONS = ["LIXADA", "PINTADA"] as const;

export type MeSurfaceCondition = (typeof ME_SURFACE_CONDITIONS)[number];

export function validateNdtCatalogItem(input: Partial<NdtCatalogItem>): string[] {
  const errors: string[] = [];
  if (!input.catalogKey || !NDT_CATALOG_KEYS.includes(input.catalogKey as NdtCatalogKey)) {
    errors.push("Informe o catálogo do registro.");
  }
  if (!input.name || !input.name.trim()) errors.push("Informe o Nome do registro.");
  return errors;
}

/** "ASME-V — ASME Sec. V Art. 23" when a code exists, otherwise just the name — never an internal id. */
export function formatCatalogItemLabel(item: NdtCatalogItem): string {
  return item.code && item.code.trim() ? `${item.code} — ${item.name}` : item.name;
}

/**
 * Only the registered code ("ASME-V"), used where the document must carry the
 * code alone. Falls back to the name when the record has no code, so the
 * field is never left empty.
 */
export function formatCatalogItemCode(item: NdtCatalogItem): string {
  return item.code && item.code.trim() ? item.code.trim() : item.name;
}

/**
 * Consolidates a stored catalog value down to its code.
 *
 * Reports issued before the code-only rule kept the full "CÓDIGO — NOME"
 * label, which reads as a duplication when both halves are the same
 * ("ASME V — ASME V"). The document must show the code alone, so the value is
 * reduced here rather than rewriting historical records.
 */
export function extractCatalogCode(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  const [first] = raw.split(/\s+[—–-]\s+/);
  return (first ?? raw).trim() || raw;
}

/**
 * Items a report may offer: ACTIVE ones applicable to the method, plus the
 * one the report is already linked to — so a historical report keeps showing
 * the value it was issued with even after the record is inactivated.
 */
export function selectableCatalogItems(
  items: readonly NdtCatalogItem[],
  catalogKey: NdtCatalogKey,
  method?: NdtMethod,
  currentlyLinkedId?: string | null
): NdtCatalogItem[] {
  return items.filter(item => {
    if (item.catalogKey !== catalogKey) return false;
    if (currentlyLinkedId && item.id === currentlyLinkedId) return true;
    if (item.status !== "ACTIVE") return false;
    if (method && item.applicableMethods.length > 0 && !item.applicableMethods.includes(method)) return false;
    return true;
  });
}
